import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import ReactDOM from 'react-dom';
import App from './App';
import { Tabs, Spin, theme } from "antd";
import { UserOutlined, AppstoreOutlined, FileTextOutlined, SendOutlined, TeamOutlined } from '@ant-design/icons';
import OrdersList from './pages/orders';
import { ConfigProvider } from "antd";
import AppHeader from './pages/header';
import "./custom-app.css";
import "./index.css";
import WysigEditor from './pages/ReportEditor/wysig';
import Login from './pages/login';
import { ErrorBoundary } from "react-error-boundary";
import MyWorklist from './pages/my-workilist';
import PacsList from './pages/pacs';
import { createTabChannel, getUserDetails, loadHeaderConfigCache } from './utils/helper';
import { TokenStorage } from './utils/cookieStorage';
import dayjs from 'dayjs';
import Settings from './pages/settings';
import DispatchList from './pages/Dispatch';
import ReportingWorkspace from './pages/Reporting';
import StatsDashboard from './pages/StatsDashboard';
import CenterAnalytics from './pages/CenterAnalytics';
import TemplateList from './pages/TemplateList';
import TinyEditor from './pages/ReportEditor/tinymce-editor';
import { BASE_API } from './axios';
import ToolUsageChart from './components/ToolUsageChart';
import SessionManagement from './pages/SessionManagement';
import { WebSocketProvider } from './context/WebSocketContext';

const MyViewer = ({ appProps }) => {
  // Define cleanup function at component level so button can access it
  const doCleanup = useRef<() => void>(() => {});

  useEffect(() => {

    let cleanupDone = false;

    // Main cleanup function
    const performCleanup = async () => {
      const win = window as any;

      try {
        // Step 0.5: Clear viewport grid to unmount all viewport components
        if (win.services?.viewportGridService) {
          try {
            const viewportGridService = win.services.viewportGridService;

            // Reset to layout with cleanup message -- must need
            viewportGridService.setLayout({
              numRows: 1,
              numCols: 1,
              layoutType: 'grid',
              findOrCreateViewport: () => ({
                displaySetInstanceUIDs: [],
                viewportOptions: {
                  viewportId: 'cleanup-viewport',
                  viewportType: 'cleanup',
                },
                displaySetOptions: [],
              }),
            });

            console.log('MPR_CLEANUP: ✅ Viewport grid cleared - React components will unmount');
          } catch (e) {
            console.warn('MPR_CLEANUP: Failed to clear viewport grid:', e);
          }
        }

        // Step 1: Disable all viewports (this triggers cache purge when remainingViewports === 0)

        // Step 3: Explicitly purge Cornerstone cache
        console.log('MPR_CLEANUP: 🗑️ Purging Cornerstone cache...');
        if (win.cornerstone?.cache) {
          // needed for sure
          try {
            win.cornerstone.cache.purgeCache();
          } catch (e) {
            console.warn('MPR_CLEANUP: Failed to purge cache:', e);
          }
        }

        // Step 4: Destroy rendering engine

        // Step 6: SWITCH TO MPR PROTOCOL
        console.log('MPR_CLEANUP: 🎯 SWITCHING TO MPR PROTOCOL...');
        try {
          const commandsManager = win.commandsManager;

          if (commandsManager) {
            console.log('MPR_CLEANUP: Calling setHangingProtocol with protocolId: "mpr"');
            try {
              const result = await commandsManager.run('setHangingProtocol', {
                protocolId: 'mpr',
                reset: true
              });

              // Wait for MPR to fully load
              // await new Promise(resolve => setTimeout(resolve, 2000));
            } catch (error) {
              console.error('MPR_CLEANUP: setHangingProtocol failed:', error);
            }
          }
        } catch (e) {
          console.error('MPR_CLEANUP: MPR protocol switch error:', e);
        }

      } catch (error) {
        console.error('❌ Cleanup error:', error);
        alert('❌ Cleanup error: ' + (error as Error).message);
      }
    };

    const wrappedCleanup = () => {
      if (cleanupDone) {
        console.log('⚠️ Cleanup already done, skipping');
        return;
      }
      cleanupDone = true;
      performCleanup();
    };

    // Expose to button
    doCleanup.current = performCleanup;

    // Handler for beforeunload - triggers browser dialog and cleanup
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      console.log('🔴 beforeunload event triggered');
      wrappedCleanup();
    };

    // Handler for unload - final cleanup attempt
    const handleUnload = () => {
      console.log('🔴 unload event triggered');
      wrappedCleanup();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleUnload);

    return () => {
      // Perform cleanup
      wrappedCleanup();

      // Remove event listeners
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleUnload);

      console.log('✅ React cleanup complete');
    };
  }, []);

  return (<div id="!pacs-app" style={{ position: 'relative' }}>
    <ErrorBoundary
      fallback={<div>Something went wrong. Please refresh the browser tab</div>}
      onError={(error) => {
        console.error('ErrorBoundary:', error);
      }}
    >
      <App config={appProps.config} defaultExtensions={appProps.defaultExtensions} defaultModes={appProps.defaultModes} />
    </ErrorBoundary>
  </div>);
};

const themeColors = [
  { name: 'violet', value: '#7D48B0' },
  { name: 'purple', value: '#AA59F7' },
  { name: 'green', value: '#2FC763' },
  { name: 'orange', value: '#FA7F36' },
  { name: 'yellow', value: '#EEC04E' }
];

// Component to handle styled tabs with theme access
const StyledTabs = ({ onTabClick, activeKey, items }) => {
  const { token } = theme.useToken();

  return (
    <Tabs
      onTabClick={onTabClick}
      activeKey={activeKey}
      style={{ background: 'white' }}
      items={items}
      tabBarStyle={{
        '--primary-color': token.colorPrimary
      } as React.CSSProperties}
    />
  );
};

function CustomApp(appProps) {
  const currentUrl = window.location.href;
  const urlParams = new URLSearchParams(window.location.search);
  const isSharedStudy = currentUrl.includes('/study-share/') ||
    urlParams.get('shared') === 'true';
  const isLoginPage = currentUrl.includes('/login');
  const isUnprotectedRoute = isSharedStudy || isLoginPage || currentUrl.includes('/no-auth/');

  // Check authentication status on mount and URL changes
  useEffect(() => {
    // Skip auth check for unprotected routes
    if (isUnprotectedRoute) {
      return;
    }

    // Check if token is valid
    const hasValidToken = TokenStorage.isTokenValid();
    const userDetails = getUserDetails();

    // If no valid token or no user details, redirect to login
    if (!hasValidToken || !userDetails) {
      TokenStorage.clearAllTokens();
      sessionStorage.clear();
      window.location.href = '/login';
    }
  }, [currentUrl, isUnprotectedRoute]);

  useEffect(() => {
    loadHeaderConfigCache().catch(() => {
        // Silently fail - will use default config
      });
  }, []);

  const userDetails = getUserDetails();
  const isTechnician = userDetails?.user_type === 'technician';
  const userType = userDetails?.user_type;
  const isHOD = userType === 'hod';
  const isConsultant =  userDetails?.consultant

  // Primary color system - defaulting to first color (violet)
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const currentTheme = themeColors[selectedColorIndex];

  const activeTabKeysMap = {
    dispatch: 'dispatch',
    technician: 'orders',
    radiologist: isConsultant ? 'worklist' : 'pacs',
    hod: isConsultant ? 'worklist' : 'pacs',
    typist: 'pacs',
    consultant: 'consultant'
  }

  const [selectedTabKey, setSelectedTabKey] = React.useState(activeTabKeysMap[userType]);
  const [appDateRange, setAppDateRange] = useState([null, null]);

  // Define tab structure first
  const tabStructure = [
    {
      key: 'orders',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <SendOutlined />
          <span>Orders</span>
        </span>
      )
    },
    {
      key: 'worklist',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <UserOutlined />
          <span>My worklist</span>
        </span>
      )
    },
    {
      key: 'tech_pacs',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <AppstoreOutlined />
          <span>All</span>
        </span>
      )
    },
    {
      key: 'pacs',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <AppstoreOutlined />
          <span>All</span>
        </span>
      )
    },
    {
      key: 'dispatch',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <SendOutlined />
          <span>Dispatch</span>
        </span>
      )
    },
    {
      key: 'consultant',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <TeamOutlined />
          <span>Consultant</span>
        </span>
      )
    },
    {
      key: 'reports',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FileTextOutlined />
          <span>Reports</span>
        </span>
      )
    },
    {
      key: 'templates',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FileTextOutlined />
          <span>Templates</span>
        </span>
      )
    }
  ];



  // Create StyledTabs component to pass as prop
  const createStyledTabsComponent = (currentItems) => {
    console.log("Creating StyledTabs with items:", currentItems);
    return (
      <StyledTabs
        onTabClick={(activeKey) => {
          if (selectedTabKey !== activeKey) {
            setSelectedTabKey(activeKey);
          }
        }}
        activeKey={selectedTabKey}
        items={currentItems}
      />
    );
  };

  console.log("selectedTabKey", selectedTabKey, tabStructure.filter(tab => ['orders', 'pacs'].includes(tab.key)));

  const worklistPacsTabs = tabStructure.filter(tab =>
    isConsultant
      ? ['worklist', ...(isHOD ? ['templates'] : [])].includes(tab.key)
      : ['worklist', 'pacs', ...(isHOD ? ['templates'] : [])].includes(tab.key)
  );

  // Memoize tab content to prevent unnecessary re-renders
  const ordersPage = useMemo(() => ({
    key: 'orders',
    label: 'Orders',
    children: <OrdersList StyledTabsComponent={() => createStyledTabsComponent(tabStructure.filter(tab => ['orders', 'tech_pacs'].includes(tab.key)))} />,
  }), [selectedTabKey]);

  const workListTab = useMemo(() => ({
    key: 'worklist',
    label: 'My worklist',
    children: <MyWorklist StyledTabsComponent={() => createStyledTabsComponent(worklistPacsTabs)} />,
  }), [selectedTabKey, isConsultant]);

  const pacsTab = useMemo(() => ({
    key: 'pacs',
    label: 'All',
    children: <PacsList StyledTabsComponent={() => createStyledTabsComponent(worklistPacsTabs)} />,
  }), [selectedTabKey, isConsultant]);

  const templatesTab = useMemo(() => ({
    key: 'templates',
    label: 'Templates',
    children: (
      <div>
        <div>{createStyledTabsComponent(worklistPacsTabs)}</div>
        <div style={{ padding: '0 1.25rem' }}>
          <TemplateList />
        </div>
      </div>
    ),
  }), [selectedTabKey, isConsultant, isHOD]);

  const pacsTabTechnician = useMemo(() => ({
    key: 'pacs',
    label: 'All',
    children: <PacsList StyledTabsComponent={() => createStyledTabsComponent(tabStructure.filter(tab => ['orders', 'tech_pacs'].includes(tab.key)))} />,
  }), [selectedTabKey]);

  const dispatchTab = useMemo(() => ({
    key: 'dispatch',
    label: 'Dispatch',
    children: <DispatchList isConsultant={false} StyledTabsComponent={() => createStyledTabsComponent(tabStructure.filter(tab => tab.key === 'dispatch'))} />,
  }), [selectedTabKey]);

  const consultantTab = useMemo(() => ({
    key: 'consultant',
    label: 'Consultant',
    children: <DispatchList isConsultant={true} StyledTabsComponent={() => createStyledTabsComponent(tabStructure.filter(tab => tab.key === 'consultant'))} />,
  }), [selectedTabKey]);

  const viewerTab = {
    key: 'viewer_pacs',
    label: 'Viewer',
    children: <MyViewer appProps={appProps} />,
    disabled: true,
  };


  const ViewerElement = () => {
    useEffect(() => {
      const channel = createTabChannel();

      channel.listenForUpdates((newUrl) => {
        window.location.href = newUrl;
      });

      return () => channel.cleanup();
    }, []);

    return (
      <ErrorBoundary
        fallback={<div>Viewer error occurred. Please refresh the browser tab</div>}
        onError={(error) => {
          console.error('ViewerElement ErrorBoundary:', error);
        }}
      >
        <MyViewer appProps={appProps} />
      </ErrorBoundary>
    );
  };

  const SharedStudyHandler = () => {
    const [state, setState] = useState({
      loading: true,
      valid: false,
      studyUID: null,
      error: null,
      studyDescription: null
    });

    useEffect(() => {
      const initializeSharedStudy = async () => {
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const isRefreshWithSharedParam = urlParams.get('shared') === 'true';

          // Case 1: Refresh with shared=true parameter - use stored validation
          if (isRefreshWithSharedParam) {
            const storedValidation = sessionStorage.getItem('sharedStudyValidation');
            if (storedValidation) {
              const validationData = JSON.parse(storedValidation);
              setState({
                loading: false,
                valid: true,
                studyUID: validationData.studyUID,
                error: null,
                studyDescription: validationData.studyDescription
              });
              return;
            } else {
              // Stored validation not found - invalid access
              setState({
                loading: false,
                valid: false,
                studyUID: null,
                error: 'Invalid shared study access',
                studyDescription: null
              });
              return;
            }
          }

          // Case 2: Fresh access with token - validate and store
          const pathname = window.location.pathname;
          const token = pathname.split('/study-share/')[1]?.split(/[/?#]/)[0];

          if (!token) {
            console.error('No token found in URL');
            setState({
              loading: false,
              valid: false,
              studyUID: null,
              error: 'Invalid study link format',
              studyDescription: null
            });
            return;
          }

          const apiUrl = `${BASE_API}/api/public/validate-study-token/${token}`;
          const response = await fetch(apiUrl);
          const result = await response.json();

          if (result.success && result.valid) {
            // Store validation in sessionStorage
            const validationData = {
              studyUID: result.study_uid,
              studyDescription: result.study_description,
              validatedAt: Date.now()
            };
            sessionStorage.setItem('sharedStudyValidation', JSON.stringify(validationData));

            // Update URL to viewer format for OHIF
            const viewerUrl = `/viewer/viewer/dicomweb?StudyInstanceUIDs=${result.study_uid}&shared=true`;
            window.history.replaceState({}, '', viewerUrl);

            setState({
              loading: false,
              valid: true,
              studyUID: result.study_uid,
              error: null,
              studyDescription: result.study_description
            });
          } else {
            console.error('Token validation failed:', result.message || 'Unknown error');
            setState({
              loading: false,
              valid: false,
              studyUID: null,
              error: result.message || 'Invalid or expired study link',
              studyDescription: null
            });
          }
        } catch (error) {
          console.error('Error in shared study initialization:', error);
          setState({
            loading: false,
            valid: false,
            studyUID: null,
            error: `API Error: ${error.message}`,
            studyDescription: null
          });
        }
      };

      initializeSharedStudy();
    }, []);

    // Loading state
    if (state.loading) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#f0f2f5'
        }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', fontSize: '16px' }}>Validating study access...</p>
        </div>
      );
    }

    // Error state
    if (!state.valid) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#f0f2f5',
          textAlign: 'center',
          padding: '20px'
        }}>
          <h2 style={{ color: '#ff4d4f', marginBottom: '16px' }}>Study Access Denied</h2>
          <p style={{ fontSize: '16px', marginBottom: '8px' }}>{state.error}</p>
          <p style={{ color: '#666', fontSize: '14px' }}>This study link may have expired or is invalid.</p>
        </div>
      );
    }

    return <RestrictedViewer studyUID={state.studyUID} studyDescription={state.studyDescription} />;
  };

  const RestrictedViewer = ({ studyUID, studyDescription }) => {
    useEffect(() => {
      window.isSharedStudyMode = true;

      return () => {
        delete window.isSharedStudyMode;
      }
    }, []);

    const restrictedAppProps = {
      ...appProps,
      config: {
        ...appProps.config,
        showStudyList: false,  // Removes Return button
        whiteLabeling: {
          ...appProps.config.whiteLabeling,
          createLogoComponentFn: () => (
            <div style={{
              color: '#ef8200',
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '8px 12px',
              background: 'rgba(239, 130, 0, 0.1)',
              borderRadius: '6px',
              border: '1px solid rgba(239, 130, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>📋</span>
              <span>SHARED STUDY</span>
            </div>
          )
        }
      }
    };

    return (
      <div className="restricted-viewer" style={{ height: '100vh', overflow: 'hidden' }}>
        {/* Optional: Custom header above OHIF */}
        {/*
        <div style={{
          background: '#f0f8ff',
          padding: '8px 16px',
          borderBottom: '1px solid #d1e7dd',
          fontSize: '12px',
          textAlign: 'center',
          color: '#666'
        }}>
          🔒 <strong style={{ color: '#ef8200' }}>Shared Study</strong> - View Only Access
          {studyDescription && (
            <span style={{ marginLeft: '8px' }}>
              • {studyDescription}
            </span>
          )}
        </div>
        */}
        {/* NO BroadcastChannel for shared studies - they should be isolated */}
        <ErrorBoundary
          fallback={<div>Restricted viewer error occurred. Please refresh the browser tab</div>}
          onError={(error) => {
            console.error('RestrictedViewer ErrorBoundary:', error);
          }}
        >
          <MyViewer appProps={restrictedAppProps} />
        </ErrorBoundary>
      </div>
    );
  };

  const RestrictedAccess = () => {
    return (
      <div>
        <div className="restricted-access">
          Restricted Page or Page Not Found
        </div>
        <div className="restrict-desc">
          The url you are trying to access is either restricted or does not
          exist.
        </div>
      </div>
    );
  };

  const PrivateRoute = ({ children, accessType }) => {
    const navigate = useNavigate();
    const token = TokenStorage.getAccessToken();

    // Check if user is authenticated
    if (!token) {
      // Redirect to login if not authenticated
      return <Navigate to="/login" replace />;
    }

    const userDetails = getUserDetails();
    const userType = userDetails?.user_type;

    if (accessType) {
      if (userType === accessType) {
        return children;
      } else {
        return <RestrictedAccess />
      }
    }
    return children;
  };

  const AppView = ({ appDateRange }) => {
    const isLoginPage = currentUrl.includes('/login');

    return (
      <BrowserRouter>
        <div className="app-content" style={{
          background: isLoginPage ? "transparent" : "#F5F7FD",
          height: isLoginPage ? '100vh' : 'calc(100vh - 3.5rem)'
        }}>
          <Routes>
            <Route path='/' element={
              <PrivateRoute>
                <>
                  {userType === 'admin' ? (
                    <Settings />
                  ) : (
                    <div style={{ background: '#F5F7FD', height: '100%' }}>
                      {selectedTabKey === 'orders' && ordersPage.children}
                      {selectedTabKey === 'worklist' && workListTab.children}
                      {selectedTabKey === 'pacs' && pacsTab.children}
                      {selectedTabKey === 'tech_pacs' && pacsTabTechnician.children}
                      {selectedTabKey === 'templates' && isHOD && templatesTab.children}
                      {selectedTabKey === 'dispatch' && dispatchTab.children}
                      {selectedTabKey === 'consultant' && consultantTab.children}
                    </div>
                  )}
                </>
              </PrivateRoute>
            }
            />
            <Route
              path="/login"
              element={
                <>
                  <Login />
                </>
              }
            />
            <Route
              path="/tinymce"
              element={
                <PrivateRoute>
                  <>
                    <TinyEditor />
                  </>
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute accessType={'admin'}>
                  <>
                    <Settings />
                  </>
                </PrivateRoute>
              }
            />
            <Route
              path="/session-management"
              element={
                <PrivateRoute accessType={'admin'}>
                  <>
                    <SessionManagement />
                  </>
                </PrivateRoute>
              }
            />
            <Route
              path="/report"
              element={
                <PrivateRoute>
                  <>
                    <ReportingWorkspace />
                  </>
                </PrivateRoute>
              }
            />
            <Route
              path="/stats-dashboard"
              element={
                <PrivateRoute>
                  <>
                    <StatsDashboard />
                  </>
                </PrivateRoute>
              }
            />
            <Route
              path="/center-analytics"
              element={
                <PrivateRoute>
                  <>
                    <CenterAnalytics />
                  </>
                </PrivateRoute>
              }
            />
            <Route
              path="/no-auth/tool-usage-analytics"
              element={
                <div style={{ padding: '24px', background: '#F5F7FD', minHeight: '100vh' }}>
                  <ToolUsageChart />
                </div>
              }
            />
            <Route
              path="/no-auth/center-analytics"
              element={
                <CenterAnalytics />
              }
            />
            {/* Add this route to handle /viewer and all its sub-routes */}
            <Route
              path="/viewer/*"
              element={<ViewerElement />}
            />
          </Routes>
        </div>
      </BrowserRouter>
    )
  };

  const handleDateChange = (val) => {
    setAppDateRange([dayjs().add(-val, 'd'), dayjs()]);
    // [dayjs().add(-7, 'd'), dayjs()]
  }

  return (
    <WebSocketProvider
      enabled={true}
      wsUrl={window.ENV_CONFIG?.WEBSOCKET_URL || 'ws://localhost:4000/api/study-updates'}
    >
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: currentTheme.value,
          },
          components: {
            Table: {
              headerBg: '#D0CCD5',
              headerColor: '#3D3D3D',
              headerSortActiveBg: '#EDE5F5',
              headerSortHoverBg: '#EDE5F5',
            }
          }
        }}
      >
        {/* Hide header for viewer, shared study, login, and no-auth routes */}
        {(currentUrl.includes('/viewer') || isSharedStudy || currentUrl.includes('/login') || currentUrl.includes('/no-auth/')) ? null : <AppHeader handleDateChange={handleDateChange} />}

        <div className={
          currentUrl.includes('/viewer') ? 'viewer-body' :
          isSharedStudy ? 'shared-viewer-body' :
          currentUrl.includes('/login') ? 'login-body' :
          currentUrl.includes('/no-auth/') ? 'no-auth-body' :
          'custom-body'
        }>
          {
            isSharedStudy ?
              <SharedStudyHandler /> :           //  Handle shared study validation
            currentUrl.includes('/viewer') ?
              <ViewerElement /> :                //  Normal authenticated viewer
              <AppView appDateRange={appDateRange} />  //  PACS interface
          }
        </div>
      </ConfigProvider>
    </WebSocketProvider>
  );
};

export default CustomApp;
